import { useParams } from 'react-router-dom';
import { useLocation } from '../../context/LocationContext';
import { useKdsStationTasks } from '../../hooks/useKdsStationTasks';
import { useKdsTaskMutations } from '../../hooks/useKdsTaskMutations';
import {
  assembleStationOrderDetail,
  isActiveStatus,
  isOngoingStatus,
  selectRecentlyDone,
  sortBoardTasks,
  taskSummaryLine,
} from './selectors';
import { useKdsBoard, type NavItem } from './useKdsBoard';
import BoardLayout from './BoardLayout';
import StationBoard from './board/StationBoard';
import SidePanel from './panels/SidePanel';
import OrderDetailSheet from './panels/OrderDetailSheet';
import StationRecentPanel, { type RecentRow } from './panels/StationRecentPanel';

/** Station view: one station's interactive board, bump/recall, recently-done + item totals. */
const StationBoardPage = () => {
  const { stationId: stationIdParam } = useParams<{ stationId: string }>();
  const stationId = Number(stationIdParam);
  const { location } = useLocation();

  const { data: stationView, isPending } = useKdsStationTasks(location.id, stationId);
  const { bump, recall, pendingTaskIds } = useKdsTaskMutations(location.id, stationId);

  const allTasks = stationView?.tasks ?? [];
  const tasks = sortBoardTasks(allTasks.filter((task) => isActiveStatus(task.status)));
  const itemTotals = stationView?.itemTotals ?? [];

  const recentRows: RecentRow[] = selectRecentlyDone(allTasks).map((task) => ({
    id: String(task.id),
    orderNo: task.bookingOrderId,
    text: taskSummaryLine(task),
    at: task.updatedUtc,
    groupId: task.groupId,
    undoTaskId: task.id,
  }));

  // One nav entry per task, so two tasks of the same order focus independently.
  const navItems: NavItem[] = tasks
    .filter((task) => Boolean(task.groupId))
    .map((task) => {
      const actionable = !pendingTaskIds.has(task.id) && (task.status === 'QUEUED' || isOngoingStatus(task.status));
      return {
        cardId: String(task.id),
        groupId: task.groupId,
        action: actionable ? () => bump(task.id) : undefined,
      };
    });

  const board = useKdsBoard(navItems);

  // The station's "Order detail (view)": this order's lines as seen here, no cross-station chips (ADR 0004).
  const detail = board.detailOpen
    ? assembleStationOrderDetail(allTasks.filter((task) => task.groupId === board.selectedGroupId))
    : null;

  return (
    <BoardLayout
      loading={board.stationsPending || isPending}
      stations={board.sortedStations}
      selection={{ kind: 'station', stationId }}
      onSelectStation={board.onSelectStation}
      connectionState={board.connectionState}
      recentOpen={board.auxOpen}
      onToggleRecent={board.onToggleRecent}
      onBoardClick={board.deselectOnOutsideClick}
      board={
        <StationBoard
          tasks={tasks}
          stationName={board.stationNameById.get(stationId) ?? `Station ${stationId}`}
          stationNameById={board.stationNameById}
          selectedGroupId={board.selectedGroupId}
          focusedCardId={board.focusedCardId}
          pendingTaskIds={pendingTaskIds}
          onOpenDetail={board.openDetail}
          onStart={bump}
          onDone={bump}
        />
      }
      detail={
        detail && (
          <SidePanel variant="detail" docked={board.isDesktop}>
            <OrderDetailSheet
              bookingOrderId={detail.bookingOrderId}
              createdUtc={detail.createdUtc}
              orderComment={detail.orderComment}
              displayItems={detail.displayItems}
              stationNameById={board.stationNameById}
              onClose={board.closeDetail}
            />
          </SidePanel>
        )
      }
      aux={
        board.auxOpen && (
          <SidePanel variant="aux" docked>
            <StationRecentPanel
              itemTotals={itemTotals}
              rows={recentRows}
              showClose={!board.isDesktop}
              onClose={board.closeAux}
              onUndo={recall}
              onOpenRow={board.openDetail}
            />
          </SidePanel>
        )
      }
    />
  );
};

export default StationBoardPage;
